/**
 * What a pass of each kind has drawn here, per model and effort — so a figure
 * printed before a press can be this browser's own receipts rather than an
 * allowance (D311).
 *
 * Every figure before a press was one allowance: twelve thousand input tokens
 * written cold and twelve thousand out, per move, whatever the model and whatever
 * the effort. It was built to lean high and did — a reasoning pass that drew
 * about 3,300 in and 4,400 out on Opus 5 was quoted at about three times what it
 * billed — and it could not move with the model: a Sonnet 5 pass at medium and
 * an Opus 5.5 pass at xhigh were quoted the same token draw at two prices. The
 * person paying asked for a figure that was accurate and that followed the
 * models chosen, and the only honest source for what a pass on THIS model at
 * THIS effort draws on THESE graphs is the passes that already ran.
 *
 * WHERE THE PASSES COME FROM. The rolling record beside the preferences, on
 * `core/passTime.ts`'s reasoning and by its watcher: "here" means this browser,
 * this key and these graphs, an exploration opened from a share link carries
 * passes drawn elsewhere under the same model stamp, and reading the stored
 * documents would mean unpacking every one of them for one number. The record
 * holds only a kind, a model id, an effort and token counts — no claim, no label.
 *
 * WHAT IT DOES NOT DO. It never prices anything that bounds: the reserve a
 * ceiling holds back before every pass (`worstPassUsd` in `engine/run.ts`) is a
 * real cap on one pass and stays one. What this changes is the figure a person
 * reads before pressing, and the range a ceiling is projected to pay for. And it
 * never reads what a model said: token counts off receipts, nothing else.
 *
 * HOW IT LEANS. Three ways, each chosen so the figure can err only upward:
 *
 *  - no figure until `DRAW_AFTER` passes of the kind on the model — below that,
 *    the caller's allowance, which is the old figure unchanged;
 *  - the `DRAW_QUANTILE` of what those passes drew, not their middle, so three
 *    presses in four come in at or under it;
 *  - a pass at a LOWER effort than the one asked for never counts. A cheaper
 *    profile's passes cannot quote a dearer one's, while a dearer profile's may
 *    stand in for a cheaper one's until the cheaper one has passes of its own.
 */

import { EFFORT_LADDER, estimateUsd, type Effort, type ModelPrice } from './models'
import { watchLandings, type OpsStore } from './landings'
import type { ModelStamp, Op } from './types'

/** The rolling record's one slot. Beside the preferences, never inside a
 *  document: it is about this browser, not about any exploration. */
export const PASS_DRAWS_KEY = 'nv.passDraws'

/**
 * How many passes of one kind on one model before a figure is drawn from them.
 * Five, on `USUAL_AFTER`'s arithmetic: the fewest passes whose range brackets
 * the typical one more than nine times in ten, with nothing assumed about how
 * the draws are spread.
 */
export const DRAW_AFTER = 5

/** How many are kept per kind and model, newest last — `USUAL_WINDOW`'s size and
 *  reason: one odd pass moves the figure by at most one place, and a change of
 *  model version or of the graphs being worked on shows within a few sittings. */
export const DRAW_WINDOW = 15

/**
 * Which of the kept draws the figure is: the upper quartile. "About" before a
 * press is read as "no more than about", and the middle of the draws would be
 * exceeded by every other press; the top of them would be the one odd pass.
 */
export const DRAW_QUANTILE = 0.75

/**
 * What a figure is drawn for. A pass's op kind, pooled where the press that
 * prices it pools them and split where it does not:
 *
 *  - `move` — one reasoning pass, whichever verb: every verb press prices as a
 *    move. The breakthrough scan and a realised synthesis are `bridge` ops,
 *    priced from their own prompt, and are not here;
 *  - `packet` — ONE ANGLE of a portfolio pass: its draw divided by its angles,
 *    because a run is quoted per move and a packet is two or three of them;
 *  - `panel` — the whole press, three seats; `audit` — one claim of a batch,
 *    because a cross-examination is priced per claim;
 *  - the three searching passes apart, since each has its own allowance. The
 *    seed's survey is priced like a source check (`SurveyCard`), so it is not
 *    recorded under a figure of its own that nothing would read;
 *  - the voice's two shapes apart, after a pass and at the end of a run.
 */
export type DrawClass =
  'move' | 'packet' | 'verify' | 'priorArt' | 'sweep' | 'panel' | 'audit' | 'voice' | 'wrapUp'

/**
 * One pass's draw, per unit its class is priced by, in the categories
 * `estimateUsd` prices. Short keys because the record is rewritten on every
 * landing; `i` is input at base (uncached, and any the provider did not name),
 * `w` a five- or thirty-minute write, `h` an hour's, `r` a cached read, `o`
 * output, `s` server requests other than page opens, `f` page opens.
 */
export interface Draw {
  e: Effort
  i: number
  w: number
  h: number
  r: number
  o: number
  s: number
  f: number
}

/** Draws per class and model, newest last. */
export type PassDraws = Record<string, Draw[]>

/** One row per class and per model — its provider as well as its id, so a
 *  custom endpoint's model named like a hosted one is not pooled with it. */
export const drawKey = (cls: DrawClass, model: Pick<ModelStamp, 'provider' | 'id'>): string =>
  `${cls}|${model.provider}:${model.id}`

/** The class a pass is recorded under, or null for a pass no press is priced
 *  from this way. */
export function drawClassOf(op: Op): DrawClass | null {
  if (op.narration) return op.narration.parentAction.kind === 'run' ? 'wrapUp' : 'voice'
  switch (op.kind) {
    case 'portfolio':
      return 'packet'
    case 'verify':
    case 'priorArt':
    case 'sweep':
    case 'panel':
    case 'audit':
      return op.kind
    // Priced from their own prompts (`bridge`), like a check (`survey`), or not
    // priced before a press at all.
    case 'bridge':
    case 'survey':
    case 'link':
    case 'research':
      return null
    default:
      return 'move'
  }
}

/** How many priced units one pass of a class holds. */
const unitsOf = (op: Op, cls: DrawClass): number =>
  cls === 'packet'
    ? Math.max(1, op.portfolio?.parts.length ?? 1)
    : cls === 'audit'
      ? Math.max(1, op.modelCalls?.length ?? 1)
      : 1

/**
 * What a pass drew, if it says what a pass like it draws: finished, neither
 * failed nor declined — a pass stopped early or turned away is not a small pass
 * — carrying its receipt, and carrying the effort its request was sent at, the
 * level after the model's own clamp. A receipt with no breakdown files all its
 * input at base and every request as a search, the dearer way.
 */
export function drawOf(op: Op): { cls: DrawClass; model: ModelStamp; draw: Draw } | null {
  if (op.finishedAt === undefined || op.error || op.refused) return null
  const spend = op.spend
  const effort = op.modelCalls?.[0]?.parameters.effort
  if (!spend || !effort || !EFFORT_LADDER.includes(effort)) return null
  const cls = drawClassOf(op)
  if (!cls) return null
  const n = unitsOf(op, cls)
  // Up, never down: a draw rounded down is a figure a little under the pass.
  const per = (x: number) => Math.ceil(Math.max(0, x) / n)
  const d = spend.detail
  return {
    cls,
    model: spend.model,
    draw: d
      ? {
          e: effort,
          i: per(d.inputUncached + d.inputUnclassified),
          w: per(d.cacheWrite5m + d.cacheWrite30m),
          h: per(d.cacheWrite1h),
          r: per(d.cacheRead),
          o: per(spend.outputTokens),
          s: per(d.webSearches + d.serverRequestsUnclassified),
          f: per(d.webFetches),
        }
      : {
          e: effort,
          i: per(spend.inputTokens),
          w: 0,
          h: 0,
          r: 0,
          o: per(spend.outputTokens),
          s: per(spend.searches),
          f: 0,
        },
  }
}

/** The record with this pass's draw added, the oldest beyond the window dropped. */
export function withPassDraw(draws: PassDraws, op: Op): PassDraws {
  const got = drawOf(op)
  if (!got) return draws
  const k = drawKey(got.cls, got.model)
  return { ...draws, [k]: [...(draws[k] ?? []), got.draw].slice(-DRAW_WINDOW) }
}

const isCount = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x) && x >= 0

/** One stored draw, or null: anything that is not a whole set of counts under a
 *  known effort is dropped rather than trusted. */
function asDraw(v: unknown): Draw | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (typeof o.e !== 'string' || !EFFORT_LADDER.includes(o.e as Effort)) return null
  const keys = ['i', 'w', 'h', 'r', 'o', 's', 'f'] as const
  if (!keys.every((k) => isCount(o[k]))) return null
  return {
    e: o.e as Effort,
    i: o.i as number,
    w: o.w as number,
    h: o.h as number,
    r: o.r as number,
    o: o.o as number,
    s: o.s as number,
    f: o.f as number,
  }
}

/** The record as stored, whatever is in the slot. */
export function readPassDraws(): PassDraws {
  let raw: unknown
  try {
    raw = JSON.parse(localStorage.getItem(PASS_DRAWS_KEY) ?? '{}')
  } catch {
    return {}
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: PassDraws = {}
  for (const [k, v] of Object.entries(raw)) {
    if (!Array.isArray(v)) continue
    const draws = v.map(asDraw).filter((d): d is Draw => d !== null)
    if (draws.length) out[k] = draws.slice(-DRAW_WINDOW)
  }
  return out
}

/* ── the record in memory, and who hears it move ─────────────────────────────
   Read on every render of every priced press, so it is parsed once and held,
   and re-read only when a pass lands here or another tab writes the slot. */

let held: PassDraws | null = null
let version = 0
const watchers = new Set<() => void>()

const moved = () => {
  version++
  for (const fn of watchers) fn()
}

/** The record, held. */
export function passDraws(): PassDraws {
  held ??= readPassDraws()
  return held
}

/** A number that changes whenever the record does — what a surface compares. */
export const passDrawsVersion = (): number => version

/** Hears the record move, for a surface that prints a figure drawn from it. */
export function onPassDrawsChange(fn: () => void): () => void {
  watchers.add(fn)
  return () => watchers.delete(fn)
}

if (typeof window !== 'undefined') {
  // Another tab's passes are this browser's too.
  window.addEventListener('storage', (e) => {
    if (e.key !== PASS_DRAWS_KEY && e.key !== null) return
    held = null
    moved()
  })
}

/** Add one finished pass. Read fresh first, so another tab's passes are kept. */
export function recordPassDraw(op: Op): void {
  if (!drawOf(op)) return
  const next = withPassDraw(readPassDraws(), op)
  held = next
  try {
    localStorage.setItem(PASS_DRAWS_KEY, JSON.stringify(next))
  } catch {
    /* private mode, or a full origin: the figure falls back to the allowance */
  }
  moved()
}

/** Record every pass this page watched run and land, by the same watcher and the
 *  same rule for "landed" as the seconds (`watchLandings`). Installed once at
 *  boot, outside React. */
export const installPassDraws = (store: OpsStore) =>
  watchLandings(store, (op) => recordPassDraw(op))

/** Forget the record — a test's reset, and nothing a person can reach. */
export function resetPassDraws(): void {
  held = null
  try {
    localStorage.removeItem(PASS_DRAWS_KEY)
  } catch {
    /* nothing held, nothing to forget */
  }
  moved()
}

/* ── the figure ──────────────────────────────────────────────────────────── */

/** A quantile by linear interpolation between the two nearest ranks. */
const quantile = (sorted: readonly number[], p: number): number => {
  const i = p * (sorted.length - 1)
  const lo = Math.floor(i)
  const hi = Math.ceil(i)
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (i - lo)
}

const rank = (e: Effort): number => EFFORT_LADDER.indexOf(e)

/** One draw as a row `estimateUsd` prices. A draw is one request's worth, so its
 *  input is its own largest request. */
const asUsage = (d: Draw) => {
  const input = d.i + d.w + d.h + d.r
  return {
    inputTokens: input,
    outputTokens: d.o,
    searches: d.s + d.f,
    peakInputTokens: input,
    detail: {
      cacheWrite5m: d.w,
      cacheWrite30m: 0,
      cacheWrite1h: d.h,
      cacheRead: d.r,
      webFetches: d.f,
    },
  }
}

/** What a figure drawn from passes says, and what it was drawn from. */
export interface DrawnFigure {
  usd: number
  /** How many passes it was drawn from. */
  passes: number
}

/**
 * What one unit of a class is expected to draw on this model at this effort, at
 * today's list — or null until there are `DRAW_AFTER` passes to draw it from, and
 * the caller prints its allowance.
 *
 * Passes at the same effort first; failing enough of those, passes at that effort
 * or dearer; never a cheaper one. Priced at the rate passed in rather than the
 * rate each pass ran at, so a price change moves the figure at once.
 */
export function drawnFigure(
  cls: DrawClass,
  model: Pick<ModelStamp, 'provider' | 'id'>,
  effort: Effort,
  price: ModelPrice,
  draws: PassDraws = passDraws(),
): DrawnFigure | null {
  const kept = draws[drawKey(cls, model)] ?? []
  const same = kept.filter((d) => d.e === effort)
  const pool = same.length >= DRAW_AFTER ? same : kept.filter((d) => rank(d.e) >= rank(effort))
  if (pool.length < DRAW_AFTER) return null
  const sorted = pool.map((d) => estimateUsd(asUsage(d), price)).sort((a, b) => a - b)
  return { usd: quantile(sorted, DRAW_QUANTILE), passes: pool.length }
}
